import React, { useEffect, useState } from 'react';
import { Header, Message, Table } from 'semantic-ui-react';
import { I18n } from 'react-redux-i18n';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import config from '../../constants';
import CurrencyHelper from '../../helpers/currencyHelper';
import { formatDateWithPattern } from '../../utils/time/formatDateWithPattern';
import { getAccountConsentEvents } from './consentApi';
import { consentContext, ConsentEvent } from './types';
import { i18nRoot } from './constants';
import styles from './ConsentAuditPanel.module.css';

dayjs.extend(utc);

// The API sends UTC datetimes without an offset marker, so parse as UTC and render in the
// admin's local time, in the unambiguous weekday format the donation view uses.
const formatConsentDate = (value: string) =>
  formatDateWithPattern(
    dayjs.utc(value).local(),
    config.INVERSION_FULL_DATE_TIME_PERIODS_FORMAT,
    '-'
  );

// The payload is a per-context JSON bag (cookie rows: consentId linking to the CookieYes hosted
// log + the category decisions). Parsed once per row and shared by the decision label and the
// details lines.
const parsePayload = (
  payload: string | null
): Record<string, unknown> | null => {
  if (!payload) {
    return null;
  }
  try {
    return JSON.parse(payload);
  } catch {
    return null;
  }
};

// Enum labels resolve through i18n by raw wire value, so a value this build doesn't know yet
// renders as the wire value rather than crashing, hiding the row, or printing the full key.
const contextLabel = (event: ConsentEvent) =>
  I18n.t(`${i18nRoot}.context.${event.context}`, {
    defaultValue: event.context
  });

const methodLabel = (event: ConsentEvent) =>
  I18n.t(`${i18nRoot}.method.${event.method}`, { defaultValue: event.method });

// Cookie consent is category-based rather than binary: essential cookies always run, so a full
// decline reads as "Essential only", and granting some categories is "Partially accepted".
const cookieDecisionLabel = (
  event: ConsentEvent,
  payload: Record<string, unknown> | null
) => {
  const categories = (payload?.categories ?? {}) as Record<string, unknown>;
  const values = Object.values(categories);
  if (values.length === 0) {
    return I18n.t(
      `${i18nRoot}.decision.${event.accepted ? 'accepted' : 'essentialOnly'}`
    );
  }
  const granted = values.filter(Boolean).length;
  if (granted === values.length) {
    return I18n.t(`${i18nRoot}.decision.accepted`);
  }
  if (granted > 0) {
    return I18n.t(`${i18nRoot}.decision.partiallyAccepted`);
  }
  return I18n.t(`${i18nRoot}.decision.essentialOnly`);
};

const decisionLabel = (
  event: ConsentEvent,
  payload: Record<string, unknown> | null
) => {
  if (event.context === consentContext.cookieConsent) {
    return cookieDecisionLabel(event, payload);
  }
  if (event.accepted === null) {
    return I18n.t(`${i18nRoot}.decision.notAsked`);
  }
  const decision = I18n.t(
    `${i18nRoot}.decision.${event.accepted ? 'accepted' : 'declined'}`
  );
  return event.explicitlyAccepted
    ? decision
    : `${decision} ${I18n.t(`${i18nRoot}.decision.implicit`)}`;
};

// Marketing consent is per charity, so the row says who it was for (organisation + campaign),
// followed by the payload rendered generically (key: value, nested objects flattened) so new
// payload shapes show up without a panel change. Keys the panel knows get a proper label.
const labelledPayloadKeys = ['wording'] as const;
const payloadLabel = (key: string) =>
  (labelledPayloadKeys as readonly string[]).includes(key)
    ? I18n.t(`${i18nRoot}.details.${key}`)
    : key;

const detailLines = (
  event: ConsentEvent,
  payload: Record<string, unknown> | null
): string[] => {
  const lines: string[] = [];
  if (event.organizationName) {
    lines.push(
      `${I18n.t(`${i18nRoot}.details.organisation`)}: ${event.organizationName}`
    );
  }
  if (event.campaignName) {
    lines.push(
      `${I18n.t(`${i18nRoot}.details.campaign`)}: ${event.campaignName}`
    );
  }
  if (payload) {
    for (const [key, value] of Object.entries(payload)) {
      if (value !== null && typeof value === 'object') {
        for (const [nestedKey, nestedValue] of Object.entries(
          value as Record<string, unknown>
        )) {
          lines.push(`${nestedKey}: ${String(nestedValue)}`);
        }
      } else {
        lines.push(`${payloadLabel(key)}: ${String(value)}`);
      }
    }
  } else if (event.payload) {
    // Unparseable payload: show it raw rather than hiding it from the audit view.
    lines.push(event.payload);
  }
  return lines;
};

// Rendered inside Details rather than as its own column: most consent events have no
// document version (cookies, 18+), so a dedicated column would sit largely empty.
const versionDetail = (event: ConsentEvent) => {
  const documentVersion = event.documentVersion;
  if (!documentVersion) {
    return null;
  }
  // A marketing row carries the privacy policy's version, not a document of its own, so it says so.
  const label =
    event.context === consentContext.marketingOptIn
      ? I18n.t(`${i18nRoot}.details.privacyPolicyDocumentVersion`)
      : I18n.t(`${i18nRoot}.details.documentVersion`);
  return (
    <div>
      {label}:{' '}
      {documentVersion.url ? (
        <a href={documentVersion.url} target="_blank" rel="noopener noreferrer">
          {documentVersion.version}
        </a>
      ) : (
        documentVersion.version
      )}
    </div>
  );
};

// Admin-entered wording (fee terms can run to 2000 characters) would swamp the table, so a long
// line shows its start and expands on request.
const detailPreviewLength = 140;

const DetailLine = ({ text }: { text: string }) => {
  const [expanded, setExpanded] = useState(false);
  if (text.length <= detailPreviewLength) {
    return <div>{text}</div>;
  }
  return (
    <div>
      {expanded ? text : `${text.slice(0, detailPreviewLength).trimEnd()}...`}{' '}
      <button
        type="button"
        className={styles.toggle}
        aria-expanded={expanded}
        onClick={() => setExpanded((value) => !value)}
      >
        {I18n.t(`${i18nRoot}.details.${expanded ? 'showLess' : 'showMore'}`)}
      </button>
    </div>
  );
};

// The fee terms an applicant agreed to sit on their own version row: the amount and whether it is
// refundable on one line, the wording (up to 2000 characters) expandable like any other long line.
const feeVersionDetail = (event: ConsentEvent) => {
  const feeVersion = event.registrationFeeVersion;
  if (!feeVersion) {
    return null;
  }
  const amount = CurrencyHelper.formatCurrency(
    feeVersion.amount,
    feeVersion.currency
  );
  const refundability = feeVersion.nonRefundable
    ? ` (${I18n.t(`${i18nRoot}.details.nonRefundable`)})`
    : '';
  return (
    <>
      <div>
        {I18n.t(`${i18nRoot}.details.registrationFeeVersion`)}: {feeVersion.id}{' '}
        ({formatConsentDate(feeVersion.createdAt)})
      </div>
      <div>
        {I18n.t(`${i18nRoot}.details.registrationFee`)}: {amount}
        {refundability}
      </div>
      <DetailLine
        text={`${I18n.t(`${i18nRoot}.details.wording`)}: ${feeVersion.terms}`}
      />
    </>
  );
};

const ConsentAuditRow = ({ event }: { event: ConsentEvent }) => {
  const payload = parsePayload(event.payload);
  const lines = detailLines(event, payload);
  const version = versionDetail(event);
  const feeVersion = feeVersionDetail(event);
  return (
    <Table.Row>
      <Table.Cell>{formatConsentDate(event.createdAt)}</Table.Cell>
      <Table.Cell>{contextLabel(event)}</Table.Cell>
      <Table.Cell>{decisionLabel(event, payload)}</Table.Cell>
      <Table.Cell>{methodLabel(event)}</Table.Cell>
      <Table.Cell>{event.ipAddress || '-'}</Table.Cell>
      <Table.Cell className={styles.details}>
        {!version && !feeVersion && lines.length === 0 && '-'}
        {version}
        {feeVersion}
        {lines.map((line, index) => (
          <DetailLine key={`${index}-${line}`} text={line} />
        ))}
      </Table.Cell>
    </Table.Row>
  );
};

interface ConsentAuditPanelProps {
  accountId: number;
}

const ConsentAuditPanel = ({ accountId }: ConsentAuditPanelProps) => {
  const [events, setEvents] = useState<ConsentEvent[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setEvents(null);
    setError(false);
    getAccountConsentEvents(accountId)
      .then((result) => {
        if (!cancelled) {
          setEvents(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  return (
    <div className={styles.panel} data-testid="consent-audit-panel">
      <Header as="h3">{I18n.t(`${i18nRoot}.title`)}</Header>
      {error && <Message negative>{I18n.t(`${i18nRoot}.error`)}</Message>}
      {!error && events === null && (
        <Message>{I18n.t(`${i18nRoot}.loading`)}</Message>
      )}
      {!error && events !== null && (
        <Table celled compact size="small">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                {I18n.t(`${i18nRoot}.headers.when`)}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {I18n.t(`${i18nRoot}.headers.consent`)}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {I18n.t(`${i18nRoot}.headers.decision`)}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {I18n.t(`${i18nRoot}.headers.method`)}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {I18n.t(`${i18nRoot}.headers.ip`)}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {I18n.t(`${i18nRoot}.headers.details`)}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {events.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={6}>
                  {I18n.t(`${i18nRoot}.empty`)}
                </Table.Cell>
              </Table.Row>
            )}
            {events.map((event) => (
              <ConsentAuditRow key={event.id} event={event} />
            ))}
          </Table.Body>
        </Table>
      )}
    </div>
  );
};

export default ConsentAuditPanel;
